import { loadBaseCSS } from '../shared/button-utils.js';
import { sanitizeUrl } from '../shared/url-utils.js';
import { escapeHtml } from '../content-blocks/qds-content-blocks.js';
import '../property-editor/qds-property-editor.js';
import { renderTrashIcon } from '../content-blocks/qds-content-icons.js';

/**
 * @element qds-card-grid-editor
 * @property {object} config - Card-grid configuration including cards
 * @property {boolean} readonly - Disables editing controls
 * @fires change - Updated card-grid configuration ({ config })
 */

loadBaseCSS();

const CARD_GRID_EDITOR_MIME = 'application/x-qds-card-grid-card';

interface QdsCardGridItem {
  eyebrow: string;
  title: string;
  description: string;
  href: string;
  // Preserve host-provided fields (e.g. `image`) that the editor doesn't
  // surface but must round-trip without data loss.
  [key: string]: unknown;
}

type QdsCardGridDragPayload =
  | { kind: 'new-card' }
  | { kind: 'existing-card'; index: number };

function cloneValue<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function toCardItem(value: unknown): QdsCardGridItem {
  const source = value && typeof value === 'object'
    ? value as Record<string, unknown>
    : {};

  return {
    // Spread first so unrecognized host fields (e.g. `image`) round-trip,
    // then normalize the fields the editor manages.
    ...source,
    eyebrow: typeof source.eyebrow === 'string' ? source.eyebrow : '',
    title: typeof source.title === 'string' ? source.title : 'New card',
    description: typeof source.description === 'string' ? source.description : '',
    href: typeof source.href === 'string' ? (sanitizeUrl(source.href) || '#') : '#'
  };
}

function createDefaultCard(): QdsCardGridItem {
  return {
    eyebrow: 'New',
    title: 'New card',
    description: 'Add supporting context for this card.',
    href: '#'
  };
}

function parseCards(value: unknown): QdsCardGridItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => toCardItem(item));
}

export class QdsCardGridEditor extends HTMLElement {
  private _config: Record<string, unknown> = {};
  private _readonly = false;
  private _selectedIndex = 0;
  private _activeDropIndex: number | null = null;
  private _activeDragPayload: QdsCardGridDragPayload | null = null;

  static get observedAttributes(): string[] {
    return ['readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClick as EventListener);
    this.shadowRoot.addEventListener('change', this._handleChange as EventListener);
    this.shadowRoot.addEventListener('dragstart', this._handleDragStart as EventListener);
    this.shadowRoot.addEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot.addEventListener('drop', this._handleDrop as EventListener);
    this.shadowRoot.addEventListener('dragend', this._handleDragEnd as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('click', this._handleClick as EventListener);
    this.shadowRoot?.removeEventListener('change', this._handleChange as EventListener);
    this.shadowRoot?.removeEventListener('dragstart', this._handleDragStart as EventListener);
    this.shadowRoot?.removeEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot?.removeEventListener('drop', this._handleDrop as EventListener);
    this.shadowRoot?.removeEventListener('dragend', this._handleDragEnd as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
      this.render();
    }
  }

  get config(): Record<string, unknown> {
    return this._config;
  }

  set config(value: Record<string, unknown> | undefined) {
    this._config = value ? cloneValue(value) : {};
    const cards = this._cards();
    if (cards.length === 0) {
      this._selectedIndex = -1;
    } else if (this._selectedIndex < 0 || this._selectedIndex >= cards.length) {
      this._selectedIndex = 0;
    }
    this.render();
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _cards(): QdsCardGridItem[] {
    return parseCards(this._config.cards);
  }

  private _setSelectedIndex(index: number): void {
    this._selectedIndex = index;
    this.render();
  }

  private _setActiveDropIndex(index: number | null): void {
    if (this._activeDropIndex === index) {
      return;
    }

    this._activeDropIndex = index;
    this.shadowRoot?.querySelectorAll('.card-grid-editor__drop-slot').forEach((slot) => {
      const slotIndex = Number.parseInt((slot as HTMLElement).dataset.dropIndex || '', 10);
      slot.classList.toggle('card-grid-editor__drop-slot--active', Number.isFinite(slotIndex) && slotIndex === index);
    });
  }

  private _resolveDropIndex(target: EventTarget | null, clientY: number): number | null {
    const element = target as HTMLElement | null;
    const slot = element?.closest<HTMLElement>('[data-drop-index]');
    if (slot) {
      const slotIndex = Number.parseInt(slot.dataset.dropIndex || '', 10);
      return Number.isFinite(slotIndex) ? slotIndex : null;
    }

    const card = element?.closest<HTMLElement>('[data-card-index]');
    if (!card) {
      return null;
    }

    const cardIndex = Number.parseInt(card.dataset.cardIndex || '', 10);
    if (!Number.isFinite(cardIndex)) {
      return null;
    }

    const rect = card.getBoundingClientRect();
    return clientY <= rect.top + (rect.height / 2) ? cardIndex : cardIndex + 1;
  }

  private _dispatchConfig(cards: QdsCardGridItem[]): void {
    this._config = {
      ...this._config,
      cards: cards.map((card) => ({ ...card }))
    };

    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      composed: true,
      detail: {
        config: cloneValue(this._config)
      }
    }));
  }

  private _commitCards(cards: QdsCardGridItem[], selectedIndex: number): void {
    const nextCards = cards.map((card) => ({ ...card }));
    this._config = {
      ...this._config,
      cards: nextCards
    };

    if (nextCards.length === 0) {
      this._selectedIndex = -1;
    } else {
      this._selectedIndex = Math.max(0, Math.min(selectedIndex, nextCards.length - 1));
    }

    this.render();
    this._dispatchConfig(nextCards);
  }

  private _appendCard(): void {
    const cards = this._cards();
    cards.push(createDefaultCard());
    this._commitCards(cards, cards.length - 1);
  }

  private _removeCard(index: number): void {
    const cards = this._cards();
    if (index < 0 || index >= cards.length) {
      return;
    }

    cards.splice(index, 1);
    this._commitCards(cards, Math.max(0, index - 1));
  }

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const actionElement = target?.closest<HTMLElement>('[data-action]');
    const cardElement = target?.closest<HTMLElement>('[data-card-index]');

    if (actionElement?.dataset.action === 'append-card') {
      if (!this._readonly) {
        this._appendCard();
      }
      return;
    }

    if (actionElement?.dataset.action === 'remove-card') {
      if (this._readonly) {
        return;
      }

      const index = Number.parseInt(actionElement.dataset.cardIndex || '', 10);
      if (Number.isFinite(index)) {
        this._removeCard(index);
      }
      return;
    }

    if (cardElement) {
      const index = Number.parseInt(cardElement.dataset.cardIndex || '', 10);
      if (Number.isFinite(index) && index !== this._selectedIndex) {
        this._setSelectedIndex(index);
      }
    }
  };

  private _handleChange = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.id !== 'card-detail-editor') {
      return;
    }

    const cards = this._cards();
    if (this._selectedIndex < 0 || this._selectedIndex >= cards.length) {
      return;
    }

    const detail = (event as CustomEvent).detail?.target || {};
    cards[this._selectedIndex] = toCardItem(detail);
    this._commitCards(cards, this._selectedIndex);
  };

  private _setDragPayload(event: DragEvent, payload: QdsCardGridDragPayload): boolean {
    if (!event.dataTransfer) {
      return false;
    }

    event.dataTransfer.setData(CARD_GRID_EDITOR_MIME, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = payload.kind === 'new-card' ? 'copy' : 'move';
    this._activeDragPayload = payload;
    return true;
  }

  private _getDragPayload(event: DragEvent): QdsCardGridDragPayload | null {
    if (!event.dataTransfer) {
      return this._activeDragPayload;
    }

    try {
      const raw = event.dataTransfer.getData(CARD_GRID_EDITOR_MIME);
      if (!raw) {
        return this._activeDragPayload;
      }

      const parsed = JSON.parse(raw) as Partial<QdsCardGridDragPayload>;
      if (parsed.kind === 'new-card') {
        return { kind: 'new-card' };
      }

      if (parsed.kind === 'existing-card' && typeof parsed.index === 'number') {
        return { kind: 'existing-card', index: parsed.index };
      }
    } catch (error) {
      console.error('Unable to read card grid drag payload.', error);
    }

    return this._activeDragPayload;
  }

  private _handleDragStart = (event: DragEvent): void => {
    if (this._readonly) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLElement | null;
    const source = target?.closest<HTMLElement>('[data-card-source], [data-card-drag-index]');
    if (!source) {
      return;
    }

    const dragIndex = Number.parseInt(source.dataset.cardDragIndex || '', 10);
    const payload: QdsCardGridDragPayload = Number.isFinite(dragIndex)
      ? { kind: 'existing-card', index: dragIndex }
      : { kind: 'new-card' };

    if (!this._setDragPayload(event, payload)) {
      event.preventDefault();
      return;
    }

    source.classList.add('card-grid-editor__item--dragging');
  };

  private _handleDragOver = (event: DragEvent): void => {
    if (this._readonly) {
      return;
    }

    const payload = this._getDragPayload(event);
    const dropIndex = this._resolveDropIndex(event.target, event.clientY);
    if (!payload || dropIndex === null) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = payload.kind === 'new-card' ? 'copy' : 'move';
    }
    this._setActiveDropIndex(dropIndex);
  };

  private _handleDrop = (event: DragEvent): void => {
    if (this._readonly) {
      return;
    }

    const payload = this._getDragPayload(event);
    const rawDropIndex = this._resolveDropIndex(event.target, event.clientY);
    if (!payload || rawDropIndex === null) {
      return;
    }

    event.preventDefault();

    const cards = this._cards();
    if (payload.kind === 'new-card') {
      cards.splice(rawDropIndex, 0, createDefaultCard());
      this._commitCards(cards, rawDropIndex);
      this._setActiveDropIndex(null);
      this._activeDragPayload = null;
      return;
    }

    if (payload.index < 0 || payload.index >= cards.length) {
      this._setActiveDropIndex(null);
      this._activeDragPayload = null;
      return;
    }

    const [movedCard] = cards.splice(payload.index, 1);
    const nextIndex = rawDropIndex > payload.index ? rawDropIndex - 1 : rawDropIndex;
    cards.splice(nextIndex, 0, movedCard);
    this._commitCards(cards, nextIndex);
    this._setActiveDropIndex(null);
    this._activeDragPayload = null;
  };

  private _handleDragEnd = (): void => {
    this._activeDragPayload = null;
    this._setActiveDropIndex(null);
    this.shadowRoot?.querySelectorAll('.card-grid-editor__item--dragging').forEach((item) => {
      item.classList.remove('card-grid-editor__item--dragging');
    });
  };

  private _bindCardDetailEditor(cards: QdsCardGridItem[]): void {
    if (!this.shadowRoot || this._selectedIndex < 0 || this._selectedIndex >= cards.length) {
      return;
    }

    const editor = this.shadowRoot.getElementById('card-detail-editor') as any;
    if (!editor) {
      return;
    }

    const card = cards[this._selectedIndex];
    editor.setAttribute('label', card.title || `Card ${this._selectedIndex + 1}`);
    editor.target = { ...card };
    editor.metadata = {
      eyebrow: { displayName: 'Eyebrow', type: 'string' },
      title: { displayName: 'Title', type: 'string' },
      description: { displayName: 'Description', type: 'textarea' },
      href: { displayName: 'Link', type: 'string' }
    };
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const cards = this._cards();
    if (cards.length === 0) {
      this._selectedIndex = -1;
    } else if (this._selectedIndex < 0 || this._selectedIndex >= cards.length) {
      this._selectedIndex = 0;
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: grid;
          gap: 10px;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        qds-property-editor {
          --qds-property-editor-margin-block: 0;
        }

        .card-grid-editor__source {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          padding: 8px 12px;
          border: 1px dashed var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: ${this._readonly ? 'default' : 'grab'};
          user-select: none;
          -webkit-user-select: none;
        }

        .card-grid-editor__source:focus-visible,
        .card-grid-editor__action:focus-visible,
        .card-grid-editor__card:focus-visible {
          outline: 2px solid var(--input-focus-border, #4285f4);
          outline-offset: 2px;
        }

        .card-grid-editor__list {
          display: grid;
          gap: 0;
        }

        .card-grid-editor__drop-slot {
          height: 12px;
          border-top: 2px solid transparent;
          transition: border-color 0.15s ease;
        }

        .card-grid-editor__drop-slot--active {
          border-color: var(--input-focus-border, #4285f4);
        }

        .card-grid-editor__card {
          display: grid;
          gap: 0;
          border: 1px solid ${this._selectedIndex >= 0 ? 'var(--theme-border-color, #e0e0e0)' : 'transparent'};
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
        }

        .card-grid-editor__card--selected {
          border-color: var(--input-focus-border, #4285f4);
          box-shadow: var(--shadow-focus, 0 1px 6px rgba(32, 33, 36, 0.2));
        }

        .card-grid-editor__card--dragging {
          opacity: 0.55;
        }

        .card-grid-editor__toolbar {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-bottom: 1px solid var(--theme-border-color, #e0e0e0);
          background: var(--theme-background-secondary, #f8f8f8);
          cursor: ${this._readonly ? 'default' : 'grab'};
          user-select: none;
          -webkit-user-select: none;
        }

        .card-grid-editor__toolbar-main {
          display: grid;
          gap: 2px;
          min-width: 0;
        }

        .card-grid-editor__eyebrow {
          color: var(--theme-text-secondary, #5f6368);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .card-grid-editor__title {
          margin: 0;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .card-grid-editor__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          min-height: 24px;
          padding: 0;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: inherit;
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .card-grid-editor__action svg {
          width: 13px;
          height: 13px;
        }

        .card-grid-editor__body {
          display: grid;
          gap: 6px;
          padding: 10px;
          cursor: pointer;
        }

        .card-grid-editor__description,
        .card-grid-editor__href {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          line-height: 1.4;
        }

        .card-grid-editor__href {
          font-family: var(--theme-font-family-mono, monospace);
          word-break: break-all;
        }

        .card-grid-editor__empty {
          display: grid;
          place-items: center;
          min-height: 72px;
          padding: 12px;
          border: 1px dashed var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          text-align: center;
        }
      </style>
      <div
        class="card-grid-editor__source"
        data-action="append-card"
        data-card-source="new"
        draggable="${this._readonly ? 'false' : 'true'}"
        title="${this._readonly ? 'Cards are read only.' : 'Drag into the list or click to append a new card.'}"
        tabindex="${this._readonly ? '-1' : '0'}"
        role="button"
        aria-label="Add card"
      >New Card</div>
      <div class="card-grid-editor__list" aria-label="Card list">
        ${cards.length === 0 ? `
          <div class="card-grid-editor__drop-slot ${this._activeDropIndex === 0 ? 'card-grid-editor__drop-slot--active' : ''}" data-drop-index="0"></div>
          <div class="card-grid-editor__empty" data-drop-index="0">Drop a new card here.</div>
        ` : cards.map((card, index) => `
          <div class="card-grid-editor__drop-slot ${this._activeDropIndex === index ? 'card-grid-editor__drop-slot--active' : ''}" data-drop-index="${index}"></div>
          <article
            class="card-grid-editor__card ${index === this._selectedIndex ? 'card-grid-editor__card--selected' : ''}"
            data-card-index="${index}"
            tabindex="0"
          >
            <div
              class="card-grid-editor__toolbar"
              data-card-drag-index="${index}"
              draggable="${this._readonly ? 'false' : 'true'}"
              aria-label="Drag card"
            >
              <div class="card-grid-editor__toolbar-main">
                ${card.eyebrow ? `<span class="card-grid-editor__eyebrow">${escapeHtml(card.eyebrow)}</span>` : ''}
                <p class="card-grid-editor__title">${escapeHtml(card.title || `Card ${index + 1}`)}</p>
              </div>
              <button
                type="button"
                class="card-grid-editor__action"
                data-action="remove-card"
                data-card-index="${index}"
                aria-label="Delete card"
                title="Delete card"
                ${this._readonly ? 'disabled' : ''}
              >
                ${renderTrashIcon()}
              </button>
            </div>
            <div class="card-grid-editor__body">
              ${card.description ? `<p class="card-grid-editor__description">${escapeHtml(card.description)}</p>` : ''}
              <p class="card-grid-editor__href">${escapeHtml(card.href || '#')}</p>
            </div>
          </article>
        `).join('')}
        <div class="card-grid-editor__drop-slot ${this._activeDropIndex === cards.length ? 'card-grid-editor__drop-slot--active' : ''}" data-drop-index="${cards.length}"></div>
      </div>
      ${this._selectedIndex >= 0 ? '<qds-property-editor id="card-detail-editor" expanded></qds-property-editor>' : ''}
    `;

    this._bindCardDetailEditor(cards);
  }
}

if (!customElements.get('qds-card-grid-editor')) {
  customElements.define('qds-card-grid-editor', QdsCardGridEditor);
}

export default QdsCardGridEditor;
